import { useState, useRef, useEffect } from 'react'
import { MessageCircle, Wifi, WifiOff, Users, Send, User, ArrowLeft } from 'lucide-react'
import { useWhatsApp, WAContact, WAMessage } from './useWhatsApp'
import { useAuth } from '../../contexts/AuthContext'

// ─── MessageBubble ────────────────────────────────────────────────────────────
function MessageBubble({ msg }: { msg: WAMessage }) {
  const out = msg.direction === 'outbound'
  const time = msg.created_at
    ? new Date(msg.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    : ''
  return (
    <div style={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start' }}>
      <div style={{
        maxWidth: '78%',
        padding: '7px 11px',
        borderRadius: out ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
        background: out ? '#dcfce7' : '#ffffff',
        border: '1px solid rgba(0,0,0,0.07)',
        fontSize: 13,
        lineHeight: 1.45,
        color: '#1a1a1a',
      }}>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.body}</div>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 2 }}>
          {msg.is_automated && <span style={{ fontSize: 10, color: '#6b7280' }}>auto</span>}
          <span style={{ fontSize: 10, color: '#9ca3af' }}>{time}</span>
        </div>
      </div>
    </div>
  )
}

// ─── ChatWindow ───────────────────────────────────────────────────────────────
function ChatWindow({ contact, msgs, onSend, onBack }: {
  contact: WAContact
  msgs: WAMessage[]
  onSend: (text: string) => void
  onBack?: () => void
}) {
  const [text, setText] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [msgs])

  const handleSend = () => {
    if (!text.trim()) return
    onSend(text.trim())
    setText('')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px', borderBottom: '1px solid var(--border)',
        background: '#f9fafb', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
      }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6b7280' }}>
            <ArrowLeft size={18} />
          </button>
        )}
        <div style={{
          width: 34, height: 34, borderRadius: contact.is_group ? 8 : '50%',
          background: contact.is_group ? '#e0f2fe' : '#25D366',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: contact.is_group ? '#0284c7' : '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0,
        }}>
          {contact.is_group ? <Users size={16} /> : <User size={15} />}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{contact.display_name || contact.phone}</div>
          {!contact.is_group && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6b7280', fontSize: 11 }}>
              {contact.phone}
            </div>
          )}
        </div>
      </div>

      {/* Messaggi */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: 12,
        display: 'flex', flexDirection: 'column', gap: 6,
        background: '#f0f2f5',
      }}>
        {msgs.length === 0 && (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 40 }}>
            Nessun messaggio
          </div>
        )}
        {msgs.map(m => <MessageBubble key={m.id} msg={m} />)}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        display: 'flex', gap: 8, padding: '8px 12px',
        borderTop: '1px solid var(--border)', background: '#fff', flexShrink: 0,
      }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
          placeholder="Scrivi un messaggio…"
          rows={2}
          style={{
            flex: 1, resize: 'none', border: '1px solid var(--border)',
            borderRadius: 8, padding: '6px 10px', fontSize: 13,
            fontFamily: 'inherit', outline: 'none',
          }}
        />
        <button
          onClick={handleSend}
          disabled={!text.trim()}
          style={{
            padding: '0 14px', background: text.trim() ? '#25D366' : '#e5e7eb',
            color: text.trim() ? '#fff' : '#9ca3af',
            border: 'none', borderRadius: 8, cursor: text.trim() ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center',
          }}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  )
}

// ─── ContactRow ───────────────────────────────────────────────────────────────
function ContactRow({ c, active, onSelect }: { c: WAContact; active: boolean; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      style={{
        width: '100%', padding: '11px 12px', border: 'none', cursor: 'pointer',
        background: active ? '#f0fdf4' : 'transparent',
        borderBottom: '1px solid #f3f4f6',
        display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: c.is_group ? 8 : '50%',
        background: active ? '#25D366' : (c.is_group ? '#e0f2fe' : '#e5e7eb'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: active ? '#fff' : (c.is_group ? '#0284c7' : '#6b7280'),
        fontWeight: 700, fontSize: 13, flexShrink: 0,
      }}>
        {c.is_group ? <Users size={17} /> : <User size={16} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {c.display_name || c.phone}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          {c.last_message_at ? new Date(c.last_message_at).toLocaleDateString('it-IT') : (c.is_group ? 'Gruppo' : c.phone)}
        </div>
      </div>
      {c.unread_count > 0 && (
        <span style={{
          background: '#25D366', color: '#fff',
          borderRadius: 99, fontSize: 10, fontWeight: 700,
          padding: '2px 7px', minWidth: 20, textAlign: 'center',
        }}>
          {c.unread_count}
        </span>
      )}
    </button>
  )
}

// ─── ContactList ──────────────────────────────────────────────────────────────
function ContactList({ contacts, activeId, onSelect }: {
  contacts: WAContact[]
  activeId: number | null
  onSelect: (id: number) => void
}) {
  const groups = contacts.filter(c => c.is_group)
  const people = contacts.filter(c => !c.is_group)

  return (
    <div style={{ width: '100%', height: '100%', overflowY: 'auto', background: '#fff' }}>
      {contacts.length === 0 && (
        <div style={{ padding: 16, color: '#9ca3af', fontSize: 12, textAlign: 'center' }}>
          Nessun contatto
        </div>
      )}
      {groups.length > 0 && (
        <>
          <div style={{ padding: '7px 12px 2px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gruppi</div>
          {groups.map(c => <ContactRow key={c.id} c={c} active={activeId === c.id} onSelect={() => onSelect(c.id)} />)}
        </>
      )}
      {people.length > 0 && (
        <>
          <div style={{ padding: '7px 12px 2px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contatti</div>
          {people.map(c => <ContactRow key={c.id} c={c} active={activeId === c.id} onSelect={() => onSelect(c.id)} />)}
        </>
      )}
    </div>
  )
}

// ─── WhatsAppInbox ────────────────────────────────────────────────────────────
export default function WhatsAppInbox() {
  const { user } = useAuth()
  const isSuperadmin = user?.role === 'superadmin'

  const {
    contacts, messages, activeContact, setActiveContact,
    connectionState, totalUnread, sendMessage,
  } = useWhatsApp()

  // non-superadmin: solo gruppi
  const visibleContacts = isSuperadmin ? contacts : contacts.filter(c => c.is_group)

  const [mobileShowChat, setMobileShowChat] = useState(false)

  const handleSelect = (id: number) => {
    setActiveContact(id)
    setMobileShowChat(true)
  }

  const handleBack = () => {
    setMobileShowChat(false)
  }

  const activeContactData = visibleContacts.find(c => c.id === activeContact) ?? null
  const activeMessages = activeContact !== null ? (messages[activeContact] ?? []) : []
  const connected = connectionState === 'open'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', background: '#25D366', color: '#fff', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15 }}>
          <MessageCircle size={20} />
          WhatsApp
          {totalUnread > 0 && (
            <span style={{ background: '#fff', color: '#25D366', borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 800 }}>
              {totalUnread}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, opacity: 0.9 }}>
          {connected ? <Wifi size={14} /> : <WifiOff size={14} />}
          {connected ? 'Connesso' : connectionState}
        </div>
      </div>

      {/* Body — layout desktop: sidebar + chat / mobile: one panel at a time */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Lista contatti — nascosta su mobile quando chat è aperta */}
        <div style={{
          width: 'clamp(200px, 30%, 260px)',
          flexShrink: 0,
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          // mobile: mostra solo se chat non aperta
          ...(mobileShowChat ? { display: 'none' } as any : {}),
        }}
          className="wa-contact-panel"
        >
          <ContactList contacts={visibleContacts} activeId={activeContact} onSelect={handleSelect} />
        </div>

        {/* Chat — su mobile occupa tutto lo schermo quando aperta */}
        <div style={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          // mobile: nasconde se nessuna chat aperta
          ...(!mobileShowChat && !activeContactData ? { display: 'none' } as any : {}),
        }}
          className="wa-chat-panel"
        >
          {activeContact !== null && activeContactData ? (
            <ChatWindow
              contact={activeContactData}
              msgs={activeMessages}
              onSend={text => sendMessage(activeContact, text)}
              onBack={handleBack}
            />
          ) : (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              color: '#9ca3af', gap: 8,
            }}>
              <MessageCircle size={40} opacity={0.3} />
              <p style={{ fontSize: 13 }}>Seleziona un contatto per visualizzare la chat</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .wa-contact-panel {
            width: 100% !important;
            border-right: none !important;
          }
          .wa-chat-panel {
            position: absolute;
            inset: 0;
            z-index: 10;
            background: #fff;
          }
        }
      `}</style>
    </div>
  )
}
